import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Globe, Linkedin, Facebook, Instagram, Download, Gift, Calendar, Mail, ExternalLink, Loader2 } from "lucide-react";
import { eventApi } from "@/lib/eventHub";
import EventLayout from "@/components/event/EventLayout";

/**
 * Public sponsor/partner/panelist profile page (Phase F).
 * Every outbound click fires a lightweight analytics beacon to /event/profiles/click
 * so the admin dashboard can show per-profile engagement metrics.
 */
export default function SponsorProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    let m = true;
    (async () => {
      try {
        const { data } = await eventApi.profile(slug);
        if (m) setProfile(data);
      } catch { if (m) setNotFound(true); }
      if (m) setLoading(false);
    })();
    return () => { m = false; };
  }, [slug]);

  const click = (kind, url) => {
    eventApi.profileClick(slug, kind);
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  if (loading) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="p-16 text-center text-emerald-800/50"><Loader2 className="mx-auto animate-spin" /></div>
      </EventLayout>
    );
  }
  if (notFound || !profile) {
    return (
      <EventLayout showProgramBanner={false}>
        <div className="mx-auto max-w-lg p-8 text-center">
          <div className="font-[Cormorant_Garamond] text-3xl text-emerald-950">Profile not found</div>
          <p className="text-sm font-[Outfit] text-emerald-800/60 mt-2">This profile may be inactive.</p>
          <Link
            to="/event/sponsors"
            data-testid="profile-notfound-back"
            className="mt-4 inline-block rounded-full bg-emerald-950 text-white px-4 h-10 leading-10 text-sm font-[Outfit] font-bold"
          >Browse sponsors</Link>
        </div>
      </EventLayout>
    );
  }

  return (
    <EventLayout showProgramBanner={false} className="bg-gradient-to-b from-white via-emerald-50/40 to-white">
      <div className="mx-auto max-w-lg px-5 py-8" data-testid={`sponsor-profile-${profile.slug}`}>
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center gap-1 text-xs font-[Outfit] font-bold uppercase tracking-widest text-emerald-800/60 hover:text-fuchsia-700 mb-4"
          data-testid="profile-back"
        >
          <ChevronLeft size={14} /> Back
        </button>

        {/* Header */}
        <div className="relative rounded-3xl overflow-hidden bg-emerald-950 text-white p-6">
          <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-fuchsia-500/30 blur-3xl" />
          <div className="relative">
            {profile.sponsor_level && (
              <div className="inline-flex items-center rounded-full bg-fuchsia-500 px-3 py-1 mb-3">
                <span className="text-[10px] tracking-[0.22em] uppercase font-[Outfit] font-bold text-white">{profile.sponsor_level}</span>
              </div>
            )}
            <h1 className="font-[Cormorant_Garamond] text-4xl leading-[1.02]">{profile.name}</h1>
            {profile.organization && (
              <div className="text-sm font-[Outfit] text-white/70 mt-1">{profile.organization}</div>
            )}
            {profile.logo_url && (
              <img
                src={profile.logo_url}
                alt={`${profile.name} logo`}
                className="mt-4 max-h-24 rounded-xl bg-white/95 p-3"
              />
            )}
          </div>
        </div>

        {/* Bio */}
        {profile.bio && (
          <div className="mt-4 rounded-2xl border border-emerald-100 bg-white p-5">
            <p className="text-sm font-[Outfit] text-emerald-950/80 leading-relaxed whitespace-pre-line">{profile.bio}</p>
          </div>
        )}

        {/* Primary CTA */}
        {profile.cta_url && (
          <button
            onClick={() => click("cta", profile.cta_url)}
            data-testid="profile-cta"
            className="mt-4 w-full h-12 rounded-full bg-fuchsia-500 hover:bg-fuchsia-600 text-white font-[Outfit] font-bold text-sm shadow-lg shadow-fuchsia-500/30 flex items-center justify-center gap-2"
          >
            {profile.cta_label || "Learn more"} <ExternalLink size={14} />
          </button>
        )}

        {/* Contact / social / resources grid */}
        <div className="mt-4 grid grid-cols-2 gap-2">
          {profile.website && <ActionTile testid="profile-website" icon={<Globe size={16} />} label="Website" onClick={() => click("website", profile.website)} />}
          {profile.linkedin && <ActionTile testid="profile-linkedin" icon={<Linkedin size={16} />} label="LinkedIn" onClick={() => click("linkedin", profile.linkedin)} />}
          {profile.facebook && <ActionTile testid="profile-facebook" icon={<Facebook size={16} />} label="Facebook" onClick={() => click("facebook", profile.facebook)} />}
          {profile.instagram && <ActionTile testid="profile-instagram" icon={<Instagram size={16} />} label="Instagram" onClick={() => click("instagram", profile.instagram)} />}
        </div>

        {/* Special offer + resource + schedule */}
        {(profile.special_offer_url || profile.special_offer) && (
          <div className="mt-4 rounded-2xl border-2 border-fuchsia-300 bg-fuchsia-50/40 p-5">
            <div className="text-[10px] uppercase tracking-widest font-[Outfit] font-bold text-fuchsia-700 flex items-center gap-1.5">
              <Gift size={12} /> Special offer for attendees
            </div>
            {profile.special_offer && <p className="mt-2 text-sm font-[Outfit] text-emerald-950/80">{profile.special_offer}</p>}
            {profile.special_offer_url && (
              <button
                onClick={() => click("offer", profile.special_offer_url)}
                data-testid="profile-offer"
                className="mt-3 rounded-full bg-fuchsia-600 hover:bg-fuchsia-700 text-white text-xs font-[Outfit] font-bold h-9 px-4"
              >
                Claim offer <ExternalLink size={12} className="inline ml-1" />
              </button>
            )}
          </div>
        )}
        {profile.resource_url && (
          <button
            onClick={() => click("resource", profile.resource_url)}
            data-testid="profile-resource"
            className="mt-3 w-full rounded-2xl border-2 border-emerald-200 bg-white hover:border-emerald-400 px-4 py-3 flex items-center gap-3 text-left"
          >
            <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><Download size={16} /></span>
            <div className="flex-1">
              <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">{profile.resource_label || "Free resource"}</div>
              <div className="text-xs font-[Outfit] text-emerald-800/60">Tap to download</div>
            </div>
          </button>
        )}
        {profile.schedule_url && (
          <button
            onClick={() => click("schedule", profile.schedule_url)}
            data-testid="profile-schedule"
            className="mt-3 w-full rounded-2xl border-2 border-emerald-200 bg-white hover:border-emerald-400 px-4 py-3 flex items-center gap-3 text-left"
          >
            <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><Calendar size={16} /></span>
            <div className="flex-1">
              <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">Schedule a conversation</div>
              <div className="text-xs font-[Outfit] text-emerald-800/60">Book time on our calendar</div>
            </div>
          </button>
        )}
        {(profile.contact_form_url || profile.email) && (
          <button
            onClick={() => click("contact", profile.contact_form_url || `mailto:${profile.email}`)}
            data-testid="profile-contact"
            className="mt-3 w-full rounded-2xl border-2 border-emerald-200 bg-white hover:border-emerald-400 px-4 py-3 flex items-center gap-3 text-left"
          >
            <span className="h-10 w-10 rounded-xl bg-emerald-100 text-emerald-800 flex items-center justify-center"><Mail size={16} /></span>
            <div className="flex-1">
              <div className="font-[Cormorant_Garamond] text-lg text-emerald-950 leading-tight">Contact us</div>
              <div className="text-xs font-[Outfit] text-emerald-800/60">Reach out directly</div>
            </div>
          </button>
        )}
      </div>
    </EventLayout>
  );
}

function ActionTile({ icon, label, onClick, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className="rounded-xl border-2 border-emerald-100 bg-white hover:border-fuchsia-300 hover:bg-fuchsia-50/40 p-3 flex items-center gap-2 text-left transition-colors"
    >
      <span className="h-8 w-8 rounded-lg bg-emerald-100 text-emerald-800 flex items-center justify-center">{icon}</span>
      <span className="text-sm font-[Outfit] font-bold text-emerald-950">{label}</span>
    </button>
  );
}
